import Head from "next/head";
import CLMHero2 from "components/CLM/CLMHero2";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import ClmShowcaseSection from "components/CLM/ShowCase";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { BASE_ASSETS_URL } from "@constants/constants";

const ActiveCard = ({
  title,
  desc,
  videoSrc,
  imgSrc,
  isImageRight = true,
  bgColor = "bg-white",
}) => {
  return (
    <div className={`flex flex-col md:flex-row gap-8 md:gap-12 md:my-12 `}>
      <div
        className={`md:w-1/3 w-full text-start flex flex-col justify-center items-start  ${
          isImageRight ? "md:order-1" : "md:order-2"
        }`}
      >
        <h2 className="text-3xl md:text-4xl font-semibold mb-4 md:mb-8">
          {title}
        </h2>
        <p className={`text-lg font-medium  md:text-xl md:mb-9`}>{desc}</p>
      </div>
      <div
        className={`md:w-2/3 w-full ${
          isImageRight ? "md:order-2" : "md:order-1"
        } flex-1`}
      >
        {videoSrc && (
          <video
            src={videoSrc}
            alt="ai-agent"
            width={500}
            height={500}
            className={`rounded-xl w-full`}
            autoPlay
            loop
            muted
          />
        )}
        {imgSrc && (
          <Image
            src={imgSrc}
            alt="ai-agent"
            width={500}
            height={500}
            className={`rounded-xl  w-full`}
          />
        )}
      </div>
    </div>
  );
};

const CLMHeroData = {
  // title: "Reporting",
  heading: "Introducing Certius",
  description:
    "Your AI legal assistant that makes reviewing and drafting contracts feel effortless.",
  // image: `${BASE_ASSETS_URL}/v2/clm/ai-assistant-hero.gif"),
  video: "/videos/ai-assistant-hero.mp4",
  btnText: "Contact Sales",
  btnUrl: "/clm/request-demo",
};

const showcaseData = [
  {
    id: 1,
    navText: "Clause Replacement",
    videoURL: "/videos/ai-assistant-instant.mp4",
    // imgSrc: `${BASE_ASSETS_URL}/v2/clm/ai-assistant-instant.gif"),
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "One-click clause replacement",
    desc: "One-click suggestions for risky clauses—based on your contract type and playbook.",
    // buttonText: "Explore the platform",
    // buttonClickURL: "/clm/",
    videoLength: 11, // in seconds
  },
  {
    id: 2,
    navText: "Form Autofill",
    videoURL: "/videos/ai-assistant-auto.mp4",
    // imgSrc: `${BASE_ASSETS_URL}/v2/clm/ai-assistant-auto.gif"),
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Auto-generate smart intake forms",
    desc: "AI transforms contract fields into dynamic questionnaires, collecting accurate inputs from business teams without legal back-and-forth.",
    // buttonText: "See how it works",
    // buttonClickURL: "/clm/",
    videoLength: 12,
  },
  {
    id: 3,
    navText: "Inline Drafting",
    videoURL: "/videos/ai-assistant-inline.mp4",
    // imgSrc: `${BASE_ASSETS_URL}/v2/clm/ai-assistant-inline.gif"),
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Inline AI drafting",
    desc: "Work directly in the document editor generate redlines, summaries, or clauses without jumping to a prompt bar.",
    // buttonText: "Learn more",
    // buttonClickURL: "/clm/",
    videoLength: 13,
  },
  {
    id: 4,
    navText: "Legal Context",
    videoURL: "/videos/ai-assistant-legal.mp4",
    // imgSrc: `${BASE_ASSETS_URL}/v2/clm/ai-assistant-legal.gif"),
    // icon: "https://sendbird.imgix.net/cms/Anywhere-logo.svg",
    heading: "Context-aware legal AI",
    desc: "AI trained on legal language, clause types, and context—so it gets what “termination for convenience” actually means.AI trained on real legal context, from indemnities to terminations—so it understands what your contracts are really saying",
    // buttonText: "Discover now",
    // buttonClickURL: "/clm/",
    videoLength: 11,
  },
];

const AIAssistent = ({ isDarkMode, setIsDarkMode }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const topTrigger = 120; // or some margin like 100
      const bottomTrigger = window.innerHeight;

      const isInView =
        rect.top <= topTrigger && rect.bottom >= bottomTrigger / 2;

      setIsDarkMode(isInView);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <>
      <Head>
        <title>Certius AI Assistant for Contract Management | BoloSign CLM</title>
        <meta
          name="description"
          content="BoloSign is an AI-powered contract management platform to draft, review, automate, and eSign contracts faster with built-in CLM."
        />
        <meta
          name="keywords"
          content="AI contract assistant, contract management AI, AI-powered CLM, contract drafting AI, contract review AI, legal AI assistant, AI contract automation, smart contract management, AI for legal teams, contract analysis AI"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/ai-assistant/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <EqualDivisionSection
        // title="Templates"
        heading="Extract contract data instantly with AI"
        description="Certius instantly pulls out the key data from every contract, like names, values, dates, and clauses. So you don’t have to dig through pages ever again."
        // image={`${BASE_ASSETS_URL}/v2/clm/ai-assistant-extract.gif")}
        video="/videos/ai-assistant-extract.mp4"
        bgColor="bg-transparent"
        imageRight={false}
        checks={[
          "No manual tagging or data entry.",
          "Auto-detects 100+ metadata points instantly.",
          "Filter, track, and report on any clause or field.",
          "Manage metadata at scale across teams and contracts.",
        ]}
        leftDivStyle="items-center md:-mt-20 md:-mb-32"
      />

      {/* showcase section */}
      <section className="bg-[#f4f2f0]">
        <div className="max-w-screen-xl md:px-8 px-4 mx-auto py-20 text-gray-900 text-center">
          {/* we will pass heading and para in it as <ClmShowcaseSection heading="" para="" /> */}
          <ClmShowcaseSection
            heading={
              <>
                Let Certius do the legal heavy <br /> lifting for You
              </>
            }
            desc="AI features built to help legal teams move faster, reduce errors, and free up time for strategic work."
            showcaseData={showcaseData}
          />
        </div>
      </section>
      <div
        className={`transition-colors duration-300  ${
          isDarkMode ? "bg-dark text-white" : "bg-white text-black"
        }`}
      >
        <section ref={sectionRef} className={`pt-20 `}>
          <div
            className={`max-w-screen-xl md:px-8 px-6 mx-auto md:py-16 py-12  text-center`}
          >
            <ActiveCard
              imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/ai-assistant-playbook.png`}
              title="Review contracts faster and smarter"
              desc="Certius helps legal teams cut review time with smart suggestions, instant redlines, and built-in compliance checks."
              isImageRight={true}
              bgColor=""
            />
          </div>
          <div
            className={`max-w-screen-xl md:px-8 px-6 mx-auto md:py-16 py-12   text-center`}
          >
            <ActiveCard
              imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/ai-assistant-ask-ai.png`}
              title="AI document assistant"
              desc="Navigate complex documents with our voice-enabled AI assistant. Get plain-English explanations of complicated terms and conditions as you go."
              isImageRight={true}
              bgColor=""
            />
          </div>
          <div
            className={`max-w-screen-xl md:px-8 px-6 mx-auto md:py-16 py-12   text-center`}
          >
            <ActiveCard
              imgSrc={`${BASE_ASSETS_URL}/v2/clm/clm/ai-assistant-improve.png`}
              title="Improve your contracts with precise edits & insights"
              desc="Certius delivers precise suggestions and contextual improvements based on industry-best language, ensuring your agreements stay clear, compliant, and review-ready."
              isImageRight={true}
              bgColor=""
            />
          </div>
        </section>
      </div>

      {/* if being used more than two places then make component */}
      <section className="px-0 md:px-8  bg-cream">
        <div className=" max-w-screen-xl mx-auto px-6 xl:px-8 md:py-32 py-16 text-black md:rounded-2xl flex flex-col md:flex-row justify-between ">
          <div className="w-full  mb-6 md:mb-0 ">
            <h2 className="text-2xl md:text-4xl xl:text-5xl font-semibold mb-4 xl:mb-8">
              Here’s how shine above uses BoloSign CLM
            </h2>
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-feedback.png`}
              alt="Customer discussion"
              className="w-[90%]"
              width={1000}
              height={1000}
            />
          </div>

          <div className="w-full md:w-2/3 ">
            <Image
              src={`${BASE_ASSETS_URL}/v2/clm/clm/contract-management-brown.png`}
              alt="Customer discussion"
              className="w-full"
              width={1000}
              height={1000}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default AIAssistent;
